import React, {
  useState,
  FormEvent,
  ChangeEvent,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import type { Swiper as SwiperType } from "swiper";

// Assets via central Assets helper
import Assets from "../../core/assets";
import { ForgetPasswordFormData } from "../../types";
import { useForgotPasswordMutation } from "../../services/admin/admin.rtk.api";
import { toast } from "../../core/toast";
import { adminRoutes } from "../../routers/AdminRouters";

const THEME_STORAGE_KEY = "theme";

const ForgetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState<Partial<{ email: string }>>({});
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoadingState, setIsLoading] = useState(false);

  // Theme state
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // helper to sync DOM classes
  const syncDomTheme = (isDark: boolean) => {
    const root = document.documentElement;
    root.classList.toggle("dark", isDark);
    document.body?.classList.toggle("dark", isDark);
  };

  // Initialize theme from localStorage or system preference
  useLayoutEffect(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as
      | "light"
      | "dark"
      | null;
    const systemPrefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;

    const initialTheme: "light" | "dark" =
      stored ?? (systemPrefersDark ? "dark" : "light");
    setTheme(initialTheme);
    syncDomTheme(initialTheme === "dark");
  }, []);

  const applyTheme = (next: "light" | "dark") => {
    setTheme(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
    syncDomTheme(next === "dark");
  };

  const toggleTheme = () => {
    applyTheme(theme === "dark" ? "light" : "dark");
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ForgetPasswordFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ForgetPasswordFormData> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const res = await forgotPassword({
        email: formData.email.trim(),
      }).unwrap();
      navigate(adminRoutes.login);
    } catch (err: any) {
      toast.error(
        err?.data?.message || err?.message || "Failed to send reset link"
      );
    }
  };

  return (
    <div
      className={`${
        theme === "dark" ? "dark " : ""
      }min-h-screen flex bg-surface text-text font-sans overflow-hidden`}
    >
      {/* Left Side - Gradient + Swiper */}
      <div
        className="hidden lg:flex lg:w-1/2 min-h-screen items-center justify-center relative"
        style={{
          background:
            theme === "dark"
              ? "linear-gradient(135deg, #232323 0%, #18191B 60%, #B6CB24 100%)"
              : "#FFFFFF",
        }}
      >
        {/* Swiper Card */}
        <div
          className={`relative z-10 w-[90%] h-[85%] rounded-3xl flex flex-col items-center justify-center shadow-2xl border ${
            theme === "dark"
              ? "bg-black border-[#232323]"
              : "bg-[#F3F4F1] border-gray-200"
          }`}
        >
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={false}
            loop
            autoplay={{ delay: 2000, disableOnInteraction: false }}
            slidesPerView={1}
            onSlideChange={(swiper: SwiperType) =>
              setActiveSlide(swiper.realIndex)
            }
            className="w-full h-full flex flex-col items-center justify-center"
            style={{ background: "transparent" }}
          >
            {[0, 1, 2].map((i) => (
              <SwiperSlide key={i}>
                <div className="flex flex-col items-center justify-center h-full">
                  <img
                    src={
                      theme === "dark"
                        ? Assets.authDarkLogo
                        : Assets.authLightLogo
                    }
                    alt="Gym Stream Logo"
                    className="w-32 h-32 object-contain"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Dots */}
          <div className="flex justify-center space-x-2 mt-8 absolute bottom-8 left-0 right-0">
            {[0, 1, 2].map((idx) => (
              <span
                key={idx}
                className={`w-8 h-1 rounded-full transition-all duration-200 ${
                  activeSlide === idx
                    ? theme === "dark"
                      ? "bg-brand-500"
                      : "bg-[#b6cb24]"
                    : theme === "dark"
                    ? "bg-white/30 border border-brand-500"
                    : "bg-[#e0e1db]"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Forget Password Form */}
      <div
        className={`w-full lg:w-1/2 min-h-screen flex items-center justify-center px-8 py-12 ${
          theme === "dark" ? "bg-surface" : "bg-[#F3F4F1]"
        }`}
      >
        <div className="w-full max-w-md space-y-8 font-sans">
          {/* Theme Toggle */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={`Switch to ${
                theme === "dark" ? "light" : "dark"
              } theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              className={`relative p-1 rounded-full transition-all duration-300 flex items-center gap-2 w-24 h-10 ${
                theme === "dark"
                  ? "bg-gray-700 border border-gray-600"
                  : "bg-gray-200 border border-gray-300"
              }`}
            >
              {/* Toggle Switch */}
              <div
                className={`absolute w-8 h-8 rounded-full transition-all duration-300 flex items-center justify-center ${
                  theme === "dark"
                    ? "translate-x-14 bg-gray-800"
                    : "translate-x-0 bg-[#B6CB24]"
                }`}
              >
                {theme === "dark" ? (
                  <img
                    src={Assets.darkModeIcon}
                    alt="Dark Mode"
                    className="w-4 h-4"
                  />
                ) : (
                  <svg
                    className="w-4 h-4 text-gray-800"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414-1.414zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>

              {/* Background Text Labels */}
              <span
                className={`absolute left-2 text-xs font-medium transition-opacity duration-300 ${
                  theme === "dark" ? "opacity-100 text-gray-300" : "opacity-0"
                }`}
              >
                DARK
              </span>
              <span
                className={`absolute right-2 text-xs font-medium transition-opacity duration-300 ${
                  theme === "light" ? "opacity-100 text-gray-600" : "opacity-0"
                }`}
              >
                LIGHT
              </span>
            </button>
          </div>

          {/* Header */}
          <div className="text-left">
            <h2
              className={`text-3xl font-bold ${
                theme === "dark" ? "text-text" : "text-gray-900"
              }`}
            >
              Forget Password
            </h2>
            <p
              className={`mt-2 ${
                theme === "dark" ? "text-muted" : "text-gray-600"
              }`}
            >
              Enter your email address and we'll send you a link to reset your
              password
            </p>
          </div>

          {/* Forget Password Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-2 ${
                  theme === "dark" ? "text-muted" : "text-gray-700"
                }`}
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder="user@mail.com"
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${
                  theme === "dark"
                    ? `placeholder-muted focus:ring-brand-500 focus:border-transparent bg-card text-text border-border ${
                        errors.email ? "border-red-500 focus:ring-red-500" : ""
                      }`
                    : `placeholder-gray-400 focus:ring-brand-500 focus:border-brand-500 bg-white text-gray-900 border-gray-300 ${
                        errors.email ? "border-red-500 focus:ring-red-500" : ""
                      }`
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center px-4 py-3 rounded-lg border border-[#C6D92C] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-bold focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-surface ${
                theme === "dark" ? "text-white" : "text-black"
              }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(122, 134, 24, 0.80) 100%)",
              }}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </div>
              ) : (
                <div className="flex items-center">
                  <img
                    src={Assets.loginArrowIcon}
                    alt="Send"
                    className={`w-5 h-5 mr-2 ${
                      theme === "light" ? "filter brightness-0" : ""
                    }`}
                  />
                  Send Reset Link
                </div>
              )}
            </button>

            {/* Back to Login */}
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-brand-500 hover:text-brand-600 transition-colors font-semibold"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
